// dashboard/ConditionMultiSelect.js
// Shared by Sidebar's "my default view" setting and FilterBar's working
// filter — most doctors only ever pick one condition, but some
// specialties (internal medicine watching CHF + OA, say) genuinely need
// more than one at once. A native <select multiple> has poor discoverability
// and an awkward ctrl/cmd-click UX, so this is a small checkbox popover
// instead, matching the checkbox styling already used elsewhere (staff
// manager/doctor toggles).

import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { c, conditionLabel } from './constants';

export default function ConditionMultiSelect({ id, options, selected, onChange, ariaLabel, openUpward }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const panelId = `${id || 'condition-filter'}-options`;

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) panelRef.current?.querySelector('input[type="checkbox"]')?.focus();
  }, [open]);

  const handlePanelKeyDown = e => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    setOpen(false);
    buttonRef.current?.focus();
  };

  const summary = selected.length === 0
    ? t('filterBar.allConditions')
    : selected.length === 1
      ? conditionLabel(selected[0])
      : `${conditionLabel(selected[0])} +${selected.length - 1} more`;

  const toggle = (ct) => {
    onChange(selected.includes(ct) ? selected.filter(x => x !== ct) : [...selected, ct]);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={ariaLabel}
        style={{
          fontSize: 12, color: c.slate, background: c.white, textAlign: 'left',
          border: `1px solid ${c.border}`, borderRadius: 8, padding: '7px 10px',
          cursor: 'pointer', minWidth: 150, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 6,
        }}
      >
        <span>{summary}</span>
        <span style={{ color: c.textSoft, fontSize: 10 }}>▾</span>
      </button>
      {open && (
        <div
          ref={panelRef}
          id={panelId}
          role="group"
          aria-label={ariaLabel}
          onKeyDown={handlePanelKeyDown}
          style={openUpward ? {
            position: 'absolute', bottom: '100%', left: 0, marginBottom: 4, zIndex: 20,
            background: c.white, border: `1px solid ${c.border}`, borderRadius: 8,
            padding: 8, minWidth: 190, boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          } : {
            position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 20,
            background: c.white, border: `1px solid ${c.border}`, borderRadius: 8,
            padding: 8, minWidth: 190, boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          }}
        >
          {options.map(ct => (
            <label key={ct} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 3px', fontSize: 12, color: c.slate, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selected.includes(ct)}
                onChange={() => toggle(ct)}
                style={{ width: 14, height: 14, accentColor: c.moss, cursor: 'pointer' }}
              />
              {conditionLabel(ct)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
